import { ArrowRight, CreditCard } from "lucide-react";
import type { ReactNode } from "react";

import { AssetAvatar } from "@/modules/dashboard/crypto-swaps/components/shared/CryptoSwapBits";
import { QuoteTimerBar } from "@/modules/dashboard/crypto-swaps/components/swap/QuoteTimerBar";
import type { OtcQuote } from "@/modules/dashboard/crypto-swaps/types/cryptoSwaps.types";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useCopTopUpFormat } from "./copTopUpFormat";

export interface CopTopUpTicketProps {
  quote: OtcQuote | null;
  /** USD the card is expected to credit. */
  credited: string | null;
  last4: string;
  isPricing: boolean;
  clockOffsetMs: number;
  /** The countdown is for the form and the review; a placed top-up has no price to watch. */
  showTimer?: boolean;
}

export function CopTopUpTicket({
  quote,
  credited,
  last4,
  isPricing,
  clockOffsetMs,
  showTimer = true,
}: CopTopUpTicketProps) {
  const { t } = useTranslations("cards");
  const format = useCopTopUpFormat();

  return (
    <div className="overflow-hidden rounded-2xl border border-border shadow-[var(--shadow-elev)]">
      <div className="dark bg-ticket text-ticket-foreground">
        <div className="flex flex-col gap-4 p-5">
          {showTimer && (
            <QuoteTimerBar
              key={quote?.id ?? "none"}
              label={t("cop_top_up_ticket_label")}
              quote={quote}
              isPricing={isPricing}
              clockOffsetMs={clockOffsetMs}
            />
          )}

          <div className={cn("flex flex-col gap-1 transition-opacity", isPricing && "opacity-60")}>
            <span className="text-[13px] text-muted-foreground">{t("cop_top_up_credit_label")}</span>
            <span className="flex items-baseline gap-2">
              <span className="font-mono text-4xl leading-none font-semibold tracking-tight tabular-nums">
                {credited ? `≈ ${format(credited, "USD", false)}` : "—"}
              </span>
              <span className="font-mono text-sm font-medium text-success-container-foreground">USD</span>
            </span>
          </div>

          <div
            className={cn(
              "grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2 transition-opacity",
              isPricing && "opacity-60",
            )}
          >
            <TrailStop
              icon={<AssetAvatar asset="COP" size="sm" />}
              label={t("cop_top_up_trail_pesos")}
              value={format(quote?.send.amount, "COP")}
            />
            <ArrowRight aria-hidden="true" className="size-4 text-muted-foreground" />
            <TrailStop
              icon={<AssetAvatar asset="USDT" kind="ONCHAIN" size="sm" />}
              label={t("cop_top_up_trail_usdt")}
              value={format(quote?.receive.amount, "USDT")}
            />
            <ArrowRight aria-hidden="true" className="size-4 text-muted-foreground" />
            <TrailStop
              icon={
                <span className="flex size-6 items-center justify-center rounded-full bg-success-container text-success-container-foreground">
                  <CreditCard aria-hidden="true" className="size-3.5" />
                </span>
              }
              label={t("cop_top_up_trail_card", { last4 })}
              value={credited ? `≈ ${format(credited, "USD")}` : "—"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TrailStop({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {icon}
      <span className="text-[11px] leading-tight text-muted-foreground">{label}</span>
      <span className="truncate font-mono text-xs font-medium tabular-nums">{value}</span>
    </div>
  );
}
